/**
 * Plain numbers for reading: a figure to fixed places with a chosen sign, a
 * compact magnitude, a count. The percent, dollar and token amount
 * formatters (`../percent`, `../usd`, `../token-amount`) build on these, so a
 * sign or a rounding rule is written once.
 *
 * A JS number in, so these are for figures that are numbers on the wire:
 * rates, ratios, scores, chart values, dollar decimals already read as
 * numbers. Where a function also takes `Units` (an exact amount in base
 * units), that input is rounded in bigint arithmetic and never through a
 * float. A token amount is never turned into a number to reach these.
 *
 * Every formatter under `src/lib/format/` is a module of its own, imported
 * from its own file and never through a barrel. Webpack keeps every export
 * of a module that any page uses, so one module per formatter is what keeps
 * each page's first load to the formatters it actually prints.
 */

/** The true minus sign (U+2212). A hyphen reads as punctuation, a minus as a loss. */
export const MINUS = '−';

/**
 * How a fixed figure carries its sign. Each style is one that screens
 * already print, named so a caller states which one it means:
 * - `hyphen`: as `toFixed` prints it, an ASCII hyphen on a negative (and
 *   "-0.0" on a negative that rounds to zero).
 * - `minus`: a true minus on any negative value, nothing otherwise.
 * - `plus-minus`: "+" on zero and above, a true minus below.
 * - `signed`: "+" above zero, a true minus below, nothing on exactly zero.
 */
type SignStyle = 'hyphen' | 'minus' | 'plus-minus' | 'signed';

export interface FixedOptions {
  /**
   * Places after the point, as `toFixed` rounds them. `auto` keeps a whole
   * value whole ("5") and gives anything else two places ("5.25"). Default 0.
   */
  digits?: number | 'auto';
  /** Default `hyphen`, which is `toFixed` unchanged. */
  sign?: SignStyle;
  /** Drop trailing zeros once rounded: "0.60" reads "0.6", "40.00" reads "40". */
  trim?: boolean;
  /** Appended as given: "%", " pp", "×". */
  unit?: string;
  /** What null or undefined reads as. Default the em dash. */
  empty?: string;
}

function placesFor(value: number, digits: FixedOptions['digits']): number {
  if (digits === 'auto') return Number.isInteger(value) ? 0 : 2;
  return digits ?? 0;
}

function fixedMagnitude(value: number, places: number, trim: boolean): string {
  const fixed = Math.abs(value).toFixed(places);
  return trim ? String(Number(fixed)) : fixed;
}

/**
 * A number to fixed places with its sign in one of the named styles and an
 * optional unit: `formatFixed(-1.234, { digits: 1, sign: 'minus', unit: '%' })`
 * reads "−1.2%". Rounding is `toFixed`'s, so a figure reads exactly as the
 * screens that printed it inline did.
 */
export function formatFixed(value: number, options?: FixedOptions): string;

export function formatFixed(
  value: number | null | undefined,
  options?: FixedOptions
): string;

export function formatFixed(
  value: number | null | undefined,
  {
    digits,
    sign = 'hyphen',
    trim = false,
    unit = '',
    empty = '—'
  }: FixedOptions = {}
): string {
  if (value == null) return empty;
  const places = placesFor(value, digits);
  if (sign === 'hyphen') {
    const fixed = value.toFixed(places);
    return `${trim ? String(Number(fixed)) : fixed}${unit}`;
  }
  const magnitude = fixedMagnitude(value, places, trim);
  let prefix = '';
  if (sign === 'minus') prefix = value < 0 ? MINUS : '';
  else if (sign === 'plus-minus') prefix = value >= 0 ? '+' : MINUS;
  else prefix = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${prefix}${magnitude}${unit}`;
}
